import { primitive } from 'serializr';

import { DataListEntry } from 'framework/entities/dataList';
import { AgentState } from 'framework/entities/agentState';
import { translator } from 'core/localization/localization';
import { createModelSchemaWrapper } from 'framework/serializr-integration';
import { CssVariables } from 'styles/cssVariables';

const i18n = translator({
	'None': {
		no: 'Ingen',
	},
	'Monitor is deactivated': {
		no: 'Monitor er deaktivert',
	},
	'Monitor in maintenance': {
		no: 'Monitor er i vedlikehold',
	},
});

export { i18n as healthDataLocalization };

export enum Severity {
	None = 'NONE',
	Minor = 'MINOR',
	Major = 'MAJOR',
	Critical = 'CRITICAL',
	Invalid = 'INVALID',
}

export enum SlaSeverity {
	Breached = 'BREACHED',
	Warning = 'WARNING',
	Compliant = 'COMPLIANT',
}

let severityDataList: Array<DataListEntry<Severity>>;

export function getSeverityDataList(): typeof severityDataList {
	if (severityDataList == null) {
		severityDataList = [
			{
				id: Severity.None,
				name: i18n('None'),
			},
			{
				id: Severity.Minor,
				name: i18n('Minor'),
			},
			{
				id: Severity.Major,
				name: i18n('Major'),
			},
			{
				id: Severity.Critical,
				name: i18n('Critical'),
			},
		];
	}

	return severityDataList;
}

export function getSeverityLabel(severity: Severity) {
	return getSeverityDataList().find((x) => x.id == severity)?.name ?? severity.toString();
}

export const healthIndexToSeverity = (healthIndex: number) => {
	if (healthIndex == null) return Severity.Invalid;

	if (healthIndex >= 75) return Severity.None;

	if (healthIndex >= 50) return Severity.Minor;

	if (healthIndex >= 25) return Severity.Major;

	if (healthIndex >= 0) return Severity.Critical;

	return Severity.Invalid;
};

export const severityToColor = (severity: Severity) => {
	switch (severity) {
		case Severity.None:
			return CssVariables.severityNone;
		case Severity.Minor:
			return CssVariables.severityMinor;
		case Severity.Major:
			return CssVariables.severityMajor;
		case Severity.Critical:
			return CssVariables.severityCritical;
		case Severity.Invalid:
			return CssVariables.severityInvalid;
	}
};

export class HealthDataHolder {
	agentState?: AgentState;
	agentDown?: boolean;
	assetMaintenance?: boolean;
	healthIndex?: number;
	assetHI?: number;
	monitorDataUnavailable?: boolean;
	monitorError?: boolean;
	monitorMaintenance?: boolean;
	monitorHI?: number;
	severity?: Severity;
	maintenanceDetails?: MaintenanceSubject[];
	active: boolean;
}

createModelSchemaWrapper(HealthDataHolder, {
	agentState: primitive(),
	agentDown: primitive(),
	assetMaintenance: primitive(),
	healthIndex: primitive(),
	assetHI: primitive(),
	monitorDataUnavailable: primitive(),
	monitorError: primitive(),
	monitorMaintenance: primitive(),
	monitorHI: primitive(),
	maintenanceDetails: primitive(),
	severity: primitive(),
	active: primitive(),
});

export enum MaintenanceSubject {
	Asset = 'ASSET',
	Monitor = 'MONITOR',
	Service = 'SERVICE',
	Agent = 'AGENT',
}

export class HealthDataFlags {
	severity: Severity;
	exclamationMark: boolean;
	wrench: boolean;
	cross: boolean;
	title: string;
	label: string;
	clock: boolean;
	arrowUp: boolean;
	arrowDown: boolean;
	arrowRight: boolean;
}

const maintenanceLabelMap = {
	[MaintenanceSubject.Asset]: 'Asset in maintenance',
	[MaintenanceSubject.Monitor]: 'Monitor in maintenance',
	[MaintenanceSubject.Service]: 'Service in maintenance',
	[MaintenanceSubject.Agent]: 'Agent in maintenance',
};

export type MetricHealthData = {
	agentState: AgentState;
	elementId: string;
	elementName: string;
	errorCode: string;
	errorText: string;
	metricError: string;
	monitorError: string;
	name: string;
	parentId: string;
	parentName: string;
	qualifierId: string;
	qualifierName: string;
	shared: boolean;
	stateIndex: number;
	targetId: string;
	targetName: string;
	timestamp: number;
	type: string;
	unitSymbol: string;
	value: string;
};

export function metricDataToFlags(item: MetricHealthData) {
	const flags = new HealthDataFlags();
	switch (item.stateIndex) {
		case 0:
			flags.severity = Severity.Critical;
			break;

		case 1:
			flags.severity = Severity.Major;
			break;

		case 2:
			flags.severity = Severity.Minor;
			break;

		case 3:
			flags.severity = Severity.None;
			break;

		default:
			flags.severity = Severity.Invalid;
			break;
	}

	const tooltip = [];

	if (item.monitorError || item.metricError || item.errorCode || item.agentState == AgentState.Warning) {
		flags.exclamationMark = true;

		if (item.type === 'MONITOR') {
			if (item.monitorError) {
				tooltip.push(i18n('Monitor error'));
			}
			if (item.metricError) {
				tooltip.push(i18n('Metric error'));
			}
			if (item.errorCode) {
				tooltip.push(item.errorText);
			}
			if (item.agentState === AgentState.Warning) {
				tooltip.push(i18n('Agent in warning'));
			}
		}
	}

	flags.title = tooltip.join(' | ');

	return flags;
}

export function healthDataToFlags(data: HealthDataHolder) {
	const flags = new HealthDataFlags();

	data = { ...data };

	data.maintenanceDetails = data.maintenanceDetails ?? [];

	if (data.assetMaintenance) {
		data.maintenanceDetails.push(MaintenanceSubject.Asset);
	}

	if (data.monitorMaintenance) {
		data.maintenanceDetails.push(MaintenanceSubject.Monitor);
	}

	if (data.agentState == AgentState.Maintenance) {
		data.maintenanceDetails.push(MaintenanceSubject.Agent);
	}

	data.healthIndex = data.healthIndex ?? data.assetHI ?? data.monitorHI;

	if (data.healthIndex !== undefined && !data.severity) {
		flags.severity = healthIndexToSeverity(data.healthIndex);
	} else if (data.severity) {
		flags.severity = data.severity;
	}

	flags.label = data.healthIndex?.toString() ?? 'N/A';

	if (data.maintenanceDetails.length > 0) {
		flags.wrench = true;
		flags.title = data.maintenanceDetails.map((x) => i18n(maintenanceLabelMap[x])).join(' | ');
	} else {
		const monitorError = data.monitorDataUnavailable
			? i18n('Monitor error: data unavailable')
			: data.monitorError
				? i18n('Monitor error')
				: undefined;

		const agentError =
			data.agentState === AgentState.Error
				? i18n('Agent in error')
				: data.agentState === AgentState.Warning
					? i18n('Agent in warning')
					: undefined;

		if (data.agentState === AgentState.Error) {
			flags.cross = true;
		}

		if (monitorError) {
			flags.title = [agentError, monitorError].filter((x) => x).join(' | ');
			flags.exclamationMark = true;
		}
	}

	flags.severity = flags.severity ?? Severity.Invalid;

	return flags;
}
